import React from 'react';

import { BUTTON_VARIANTS } from 'lib/button';
import { BREAKPOINTS, getRem } from 'lib/core';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Toast } from './Toast';
import { TOAST_POSITIONS, TOAST_VARIANTS, TOAST_VISIBILITY_DURATIONS } from '../constants';

const StyledMultiToast = styled.div`
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zIndex.toast.value};
  ${({ $position }) =>
    $position === TOAST_POSITIONS.FIXED &&
    css`
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      left: ${({ theme }) => theme.size.spacing.medium.value};
      position: fixed;
      right: ${({ theme }) => theme.size.spacing.medium.value};
      top: ${({ theme }) => theme.size.spacing.medium.value};
      width: auto;

      @media ${BREAKPOINTS.L} {
        left: auto;
        max-width: ${getRem('372px')};
        width: 100%;
      }
    `}
`;

const MultiToasts = React.forwardRef(({ className, items = [], position = TOAST_POSITIONS.INLINE, ...other }, ref) =>
  items.length ? (
    <StyledMultiToast $position={position} {...other} ref={ref}>
      {items.map((item, index) => (
        <Toast className={className} key={item.id} {...item} isMulti position={position} />
      ))}
    </StyledMultiToast>
  ) : null
);

MultiToasts.propTypes = {
  /** Adds classname for additional styling */
  className: PropTypes.string,
  /** Array of toast component props */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** Array of action elements. Action element contains Button component properties.
       * A maximum of 2 actions is allowed. */
      actions: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          onClick: PropTypes.func,
          /** Changes button style depending on variant */
          variant: PropTypes.oneOf([
            BUTTON_VARIANTS.ICON,
            BUTTON_VARIANTS.PRIMARY,
            BUTTON_VARIANTS.SECONDARY,
            BUTTON_VARIANTS.TEXT,
          ]),
          icon: PropTypes.node,
        })
      ),
      /** The content of the component */
      content: PropTypes.node.isRequired,
      /** Unique id for component */
      id: PropTypes.string.isRequired,
      /** Toast visibility handler when visibility duration ends */
      onVisibilityDurationEnd: PropTypes.func,
      /** Inline styles applied to main component wrapper */
      style: PropTypes.shape({}),
      /** Changes toast style depending on variant. */
      variant: PropTypes.oneOf(Object.values(TOAST_VARIANTS)),
      /** Duration of time for how long component will be visible */
      visibilityDuration: PropTypes.oneOf(Object.values(TOAST_VISIBILITY_DURATIONS)),
    })
  ),
  /** Sets components position accordingly to specified value */
  position: PropTypes.oneOf(Object.values(TOAST_POSITIONS)),
};

export { MultiToasts };
